import { z } from 'zod';
import { EnrollmentType } from './enrollment-type.js';
import { DepositBranchSchema } from './credit.js';
import { FunnelDepositState } from './deposit.js';
/**
 * Consent, click-wrap sign & instant-accept contract (E-SF06) — the shapes for
 * `GET /bff/sign/context` and `POST /bff/sign/accept`.
 *
 * This is the INTERFACE the at-accept saga (built separately) implements; it
 * exists now so US-Shop's sign surface has something TYPED to call. Where the BFF
 * route is not wired yet, the SPA fails loud / shows a pending state — it never
 * fakes a signed success (US fail-loud posture; ADR-0009).
 *
 * ── WHY THESE LIVE IN THE CONTRACT ─────────────────────────────────────────
 * They are the one edge between the SPA and the BFF (ADR-0002 §4). The BFF uses
 * them as its `response` schema (the boot-time response guard walks them), and
 * US-Shop imports them for typed fetchers. Neither response declares `tenantId`,
 * a credential, or PII (`ssn`/`dob`/`pan`/`esiid`) — the guard forbids all of
 * those, and these shapes are written to satisfy it.
 *
 * ── RATIFIED PO DECISIONS (ADR-0009 R-1 RESOLUTION, 2026-07-19) ─────────────
 *  1. `web_self_serve` renders the **domestic (residential)** EFL/TOS/YRAC set —
 *     NOT the `plan`→small-business set. `SignContext.customerClass` asserts
 *     `RESIDENTIAL` so the FE proves it renders the domestic disclosures.
 *  2. A REQUIRED deposit is **collected (settled) before accept**. The
 *     `DepositGate` carries a SERVER-AUTHORITATIVE `satisfied` flag; the FE gates
 *     the accept control on it and never recomputes it (see the gate note below).
 */
// ─────────────────────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────────────────────
/** ISO calendar date (YYYY-MM-DD). Service dates are Texas (America/Chicago) days. */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'date must be YYYY-MM-DD');
/** The three PUCT §25.475 disclosure documents the customer click-wraps. */
export const SignedDocumentKind = z.enum(['EFL', 'TOS', 'YRAC']);
// ─────────────────────────────────────────────────────────────────────────────
// GET /bff/sign/context — what the sign page reads
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Start-date constraints, computed SERVER-SIDE (real Texas business-day + AMS
 * cutoff math; NEVER `+N days` in the browser). The FE renders these and lets the
 * customer pick within them; it does not compute dates itself.
 *
 *  - SWITCH  → the customer picks "first available" (`firstAvailableDate`) or a
 *              chosen date within [`earliestDate`, `latestDate`].
 *  - MOVE_IN → the customer picks a move-in date within the same window;
 *              `amsSameDayAvailable` is true only when the premise has an AMS
 *              (smart) meter AND the request is before the daily cutoff — so a
 *              same-day start is still physically possible today.
 */
export const StartDateConstraintsSchema = z.object({
    enrollmentType: EnrollmentType,
    /** Earliest selectable service start date (inclusive). */
    earliestDate: IsoDateSchema,
    /** Latest selectable service start date (inclusive). */
    latestDate: IsoDateSchema,
    /** SWITCH: the "first available" date. MOVE_IN: the earliest move-in date. */
    firstAvailableDate: IsoDateSchema,
    /**
     * MOVE_IN + AMS meter only: whether a same-day start is still possible right
     * now (server evaluates the AMS daily cutoff in America/Chicago). Always false
     * for switches and for non-AMS meters.
     */
    amsSameDayAvailable: z.boolean(),
    /** Human-facing cutoff label for the same-day rule (e.g. "5:00 PM CT"). */
    sameDayCutoffLabel: z.string().max(40).optional(),
});
/**
 * The disclosure documents the customer must click-wrap. Text + a version ref per
 * document so the SignatureEvent can ASSOCIATE the signature with the exact record
 * accepted (ESIGN attribution). `customerClass` is `RESIDENTIAL` — the ratified
 * domestic disclosure set (NOT small-business).
 *
 * ⚠️ Pre-publish: for the seeded catalogue the text is a PREVIEW placeholder and
 * the FE renders it behind the `VITE_FEATURE_EFL_PUBLISH` gate as a badged
 * PREVIEW (E-SF02 parity) — never as a live PUCT §25.475 document.
 */
export const SignDisclosuresSchema = z.object({
    /** Domestic (residential) disclosures — the ratified web_self_serve class. */
    customerClass: z.literal('RESIDENTIAL'),
    eflText: z.string(),
    tosText: z.string(),
    yracText: z.string(),
    /** Version/hash refs, echoed back in the SignatureEvent documents[]. */
    eflVersion: z.string().min(1),
    tosVersion: z.string().min(1),
    yracVersion: z.string().min(1),
});
/**
 * The deposit-settlement gate (ratified PO decision 2 — collected before accept).
 *
 * ── WHY `satisfied` IS SERVER-AUTHORITATIVE ────────────────────────────────
 * The FE gates the accept control on `satisfied` and NEVER recomputes it from
 * `state`. Whether a low-income INSTALLMENT (partial payment) counts as "settled
 * for accept" is a §25.478 product decision owned by the saga / PO, not the
 * browser — so the server decides and the client obeys. A REQUIRED-but-unsettled
 * deposit (`required && !satisfied`) blocks accept; the FE renders the inline
 * `deposit` branch for collection until the server reports `satisfied: true`.
 *
 * ⚠️ COUNSEL RISK (attested ≠ verified): when the gate is satisfied via a WAIVER
 * (`state === 'DEPOSIT_WAIVED'`/`'DEPOSIT_INSTALLMENT_SCHEDULED'`), the election
 * is ATTESTED, not verified. The FE copy must say "subject to verification" and
 * must NOT represent the waiver as verified. Do not resolve attested-vs-verified
 * in code — it is counsel-gated (E-SF05 stub 24).
 */
export const DepositGateSchema = z.object({
    /**
     * ⚠️ WHETHER A §25.478 DEPOSIT DECISION EXISTS AT ALL (E-SF19).
     *
     * `required: false` used to carry two completely different meanings that no
     * consumer could tell apart: "the credit check ran and this customer owes
     * nothing", and "no credit check has run, so there is no deposit record to
     * report". The gate is built from a candidate-keyed deposit record, and that
     * record is only written when a deposit IS owed — so an approved-with-no-deposit
     * customer and a customer who has not reached the credit step produce the same
     * `{ required: false, satisfied: true }`.
     *
     * The storefront rendered "No deposit required — you have no deposit to pay" for
     * both. QA reached the sign step by direct URL on a session at step `plan` and was
     * told, in a success alert, that they owed no deposit — before any §25.478
     * decision existed to say so.
     *
     * So the decision's EXISTENCE is now stated separately from its outcome. When
     * this is false the SPA must assert NOTHING about a deposit; `required` and
     * `satisfied` are not yet answers.
     */
    decided: z.boolean(),
    /** Whether a deposit is required for this enrollment at all. */
    required: z.boolean(),
    /** The funnel deposit sub-state. Null when no deposit is required. */
    state: FunnelDepositState.nullable(),
    /**
     * SERVER-AUTHORITATIVE: whether the gate permits accept right now. The FE gates
     * the accept button on this and never derives it from `state`.
     */
    satisfied: z.boolean(),
    /**
     * The deposit branch (amount, dueBy, offered waivers) — present only when a
     * deposit is required and still unsettled, so the sign page can render inline
     * collection. Absent (null) once settled or when no deposit is required.
     */
    deposit: DepositBranchSchema.nullable(),
});
/**
 * `GET /bff/sign/context` response — everything the sign page needs to render the
 * consent + start-date + deposit surface for the caller's session-resolved quote.
 *
 * No `tenantId`, no credential, no PII: the BFF resolves the tenant from Host and
 * the prospect/quote from the opaque `candidateId` in the session.
 */
export const SignContextResponseSchema = z.object({
    /** SWITCH vs MOVE_IN (drives the start-date picker + the rescission notice). */
    enrollmentType: EnrollmentType,
    /** The domestic (residential) disclosures to click-wrap. */
    disclosures: SignDisclosuresSchema,
    startDate: StartDateConstraintsSchema,
    depositGate: DepositGateSchema,
});
// ─────────────────────────────────────────────────────────────────────────────
// POST /bff/sign/accept — the SignatureEvent submission
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The customer's start-date selection (FE → server), discriminated on the
 * server-derived `enrollmentType` so the two modes cannot be conflated:
 *
 *  - SWITCH  → `mode: 'FIRST_AVAILABLE'` (no date) OR `mode: 'CHOSEN'` with `date`.
 *  - MOVE_IN → `date` is always the chosen move-in date; `requestSameDay` is the
 *              customer's same-day INTENT (honoured server-side only when the
 *              premise's AMS meter + cutoff allow it — never trusted blindly).
 */
export const StartDateSelectionSchema = z
    .discriminatedUnion('enrollmentType', [
    z.object({
        enrollmentType: z.literal('SWITCH'),
        mode: z.enum(['FIRST_AVAILABLE', 'CHOSEN']),
        /** Required when `mode === 'CHOSEN'`; omitted for first-available. */
        date: IsoDateSchema.optional(),
    }),
    z.object({
        enrollmentType: z.literal('MOVE_IN'),
        /** The chosen move-in date (always explicit for a move-in). */
        date: IsoDateSchema,
        /** Same-day AMS start intent; server honours it only when eligible. */
        requestSameDay: z.boolean(),
    }),
])
    .superRefine((value, ctx) => {
    if (value.enrollmentType === 'SWITCH' && value.mode === 'CHOSEN' && value.date === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'a chosen switch date requires `date`',
            path: ['date'],
        });
    }
});
/**
 * Affirmative click-wrap acceptance of the three disclosures. Each is
 * `literal(true)`: "accepted the EFL/TOS/YRAC" is a required affirmative act, so
 * "signed without accepting a disclosure" is un-representable in the contract (the
 * `CreditConsentSchema` precedent). Nothing here is pre-checked or defaulted true.
 */
export const ConsentAcceptancesSchema = z.object({
    /** Reviewed & accepted the Electricity Facts Label. */
    efl: z.literal(true),
    /** Reviewed & accepted the Terms of Service. */
    tos: z.literal(true),
    /** Reviewed & accepted Your Rights as a Customer. */
    yrac: z.literal(true),
});
/** One document the customer accepted, by kind + the version they saw. */
export const SignedDocumentRefSchema = z.object({
    kind: SignedDocumentKind,
    /** The disclosure version/hash accepted (from `SignContext.disclosures.*Version`). */
    version: z.string().min(1).max(200),
});
/**
 * Hard cap on the ENCODED drawn-signature payload, in characters (E38).
 *
 * 256 KB decoded is the durable-storage limit — `quotation.signature_event` is
 * append-only (the trigger raises P0001 on UPDATE and DELETE alike), so a row
 * written there can never be shrunk or cleaned up and an uncapped image column
 * is a storage-exhaustion vector with no remediation short of a migration.
 * Base64 expands by 4/3; the margin covers the `data:image/png;base64,` prefix,
 * padding, and 76-column line wrapping.
 *
 * Kept numerically in lockstep with `SIGNATURE_IMAGE_MAX_BASE64_CHARS` in
 * Quotation, which is the enforcing boundary.
 */
export const SIGNATURE_IMAGE_PNG_MAX_BYTES = 256 * 1024;
export const SIGNATURE_IMAGE_PNG_MAX_BASE64_CHARS = Math.ceil(SIGNATURE_IMAGE_PNG_MAX_BYTES / 3) * 4 + 128;
/**
 * The `SignatureEvent` submission (E-SF06). A click-wrap e-signature under the
 * ESIGN Act (NOT eIDAS — US adapter locked decision): the affirmative act is the
 * `clickwrap.agreed: literal(true)` + the sign control the customer presses.
 *
 * ── NO PII RE-COLLECTED ────────────────────────────────────────────────────
 * The customer's identity (name/DOB/SSN) was captured at the credit step
 * (E-SF05) and lives server-side on the prospect, keyed by the opaque
 * `candidateId`. This event carries NO SSN/DOB/name — the click IS the signature
 * and the server attributes it to the already-captured prospect. The BFF resolves
 * `candidateId` → session → prospect + DRAFT quote before flipping to SIGNED.
 *
 * `tcpaSmsConsent` is an explicit OPT-IN and is a plain boolean (may be false) —
 * it is NOT `literal(true)`: SMS marketing consent must be defeasible and
 * unchecked by default (TCPA). It rides here so the accept records it atomically.
 */
export const SignatureEventSchema = z.object({
    /** The opaque, session-scoped premise handle (E-SF03). Never the raw ESIID. */
    candidateId: z.string().min(1),
    /** The shopped tariff. */
    planId: z.string().min(1).max(255),
    startDate: StartDateSelectionSchema,
    consents: ConsentAcceptancesSchema,
    /** Explicit TCPA SMS opt-in. Defaults to false; the customer must check it. */
    tcpaSmsConsent: z.boolean(),
    /**
     * OPTIONAL drawn signature (E38 / ADR-0010 D5), as a PNG — either raw base64
     * or the exact `data:image/png;base64,` form `canvas.toDataURL('image/png')`
     * produces. Absent for every typed-only signature, which is the normal case.
     *
     * ── CORROBORATING, NEVER OPERATIVE ──────────────────────────────────────
     * The drawn mark does NOT make the signature. The affirmative act is still
     * `clickwrap.agreed` plus the typed name the server already holds against the
     * prospect, and `signature_event_signer_name_present_check` keeps that name
     * mandatory on every evidence row. 15 U.S.C. §7001 does not require a drawn
     * mark, and the record this funnel already produces — click-wrap consent,
     * typed name, `disclosureVersion`, a server-stamped consent timestamp,
     * signer IP + User-Agent, and SHA-256 over the actual EFL/TOS/YRAC bytes — is
     * stronger evidence than most drawn signatures. This field is a product/UX
     * feature (ADR-0010 D5.6), so its absence degrades nothing.
     *
     * ── THIS CAP IS NOT THE CONTROL ─────────────────────────────────────────
     * The bound below is a cheap early reject so an oversized body dies before it
     * crosses a network hop. The ENFORCING boundary is Quotation's
     * `validateSignatureImagePng`, which additionally sniffs the PNG magic bytes,
     * walks the chunk stream to reject appended trailing data, and bounds the
     * IHDR dimensions and total pixel count. It must stay authoritative because
     * the CRM path reaches accept WITHOUT traversing this BFF, so a check that
     * lived only here would cover one of the two producers. Keep this a length
     * bound only — do not grow it into a second, divergent validator.
     *
     * 256 KB decoded, expressed as its base64 ceiling (4/3 expansion) plus room
     * for the data-URI prefix, padding and line wrapping. Mirrors
     * `SIGNATURE_IMAGE_MAX_BASE64_CHARS` in Quotation.
     */
    signatureImagePng: z.string().min(1).max(SIGNATURE_IMAGE_PNG_MAX_BASE64_CHARS).optional(),
    clickwrap: z.object({
        /** The affirmative intent-to-sign act. `literal(true)` — cannot be omitted. */
        agreed: z.literal(true),
        /**
         * Client-observed acceptance instant (ISO-8601 with offset). The SERVER
         * records its own authoritative timestamp; this is the corroborating client
         * attestation, not the system of record.
         */
        acceptedAt: z.string().datetime({ offset: true }),
        /** The exact disclosure versions the customer accepted (ESIGN association). */
        documents: z.array(SignedDocumentRefSchema).min(1),
    }),
});
/**
 * `POST /bff/sign/accept` result. Success-only: a rejected accept (deposit gate
 * unsatisfied, stale quote, missing account) is a non-2xx ProblemDetails, never a
 * "declined" success branch — so a signed success is unambiguous.
 *
 * No `tenantId`, no account id, no quote id, no PII: the anonymous caller gets an
 * unguessable `statusToken` for the E-SF07 status page and nothing enumerable.
 */
export const AcceptResultSchema = z.object({
    status: z.literal('accepted'),
    /**
     * Opaque, unguessable, single-purpose token for the anonymous status page
     * (E-SF07). NOT the quote/account id — an enumeration-safe handle.
     */
    statusToken: z.string().min(1),
    /** The service start date the accept locked in (ISO calendar date). */
    serviceStartDate: IsoDateSchema,
    /**
     * SWITCH only: the 3-business-day right-of-rescission deadline (server-computed,
     * real Texas business days). Null for a move-in (no switch rescission window).
     */
    rescissionDeadline: IsoDateSchema.nullable(),
});
//# sourceMappingURL=sign.js.map